import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { NavigationEnd, Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { AuthService } from '../services/auth.service';
import { ProfileService } from '../profile/profile.service';
import { ProfileDetails } from '../shared/interfaces/profileDetails-response.interface';
import { DashboardHttpService } from '../dashboard/common/services/dashboard-http.service';
import { catchError, map, of } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  title = '';
  loginDisplay = false;
  loginText = 'Login';
  private readonly _destroying$ = new Subject<void>();
  loginForm: FormGroup;
  loading = false;
  submitted = false;
  returnUrl: string;
  error = '';
  rememberMe: boolean = false;
  userDetails: ProfileDetails;
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private authService: AuthService,
    private profileService: ProfileService,
    private dashboardHttpService: DashboardHttpService,
  ) {}

  ngOnInit(): void {
    if (this.isLoggedIn()) {
      this.resolvePostLoginRoute().subscribe((route) => this.router.navigate([route]));
    }
    this.rememberMe = this.isRememberMe();
    this.initLoginForm();
  }

  initLoginForm() {
    this.loginForm = this.fb.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
    });
  }

  private isLoggedIn(): boolean {
    return this.authService.isAuthenticated();
  }

  get f() {
    return this.loginForm.controls;
  }

  onSubmit() {
    if (this.loginForm.invalid) return;
    const { username, password } = this.loginForm.value;
    this.loginText = 'Please Wait ...';
    this.authService.login({ username, password }).subscribe(
      (response) => {
        this.authService.setAuthenticated(true);
        this.authService.rememberMe(this.rememberMe);
        this.getUserProfileData();

        const returnUrl = this.route.snapshot.queryParams['returnUrl'] || null;
        if (returnUrl) {
          this.router.navigateByUrl(returnUrl);
        } else {
          this.resolvePostLoginRoute().subscribe((route) => this.router.navigate([route]));
        }
      },
      (error) => {
        console.error(error);
        this.loginText = 'Login';
        Swal.fire({
          icon: 'error',
          text: error.error?.message || error.message || 'Login failed',
          showConfirmButton: true,
          confirmButtonText: 'Ok',
        });
      },
    );
  }

  private resolvePostLoginRoute() {
    return this.dashboardHttpService.getProspect().pipe(
      map((prospect) => (prospect && prospect.id != null ? '/statement' : '/dashboard')),
      catchError(() => of('/dashboard')),
    );
  }

  getUserProfileData() {
    this.profileService.getUserProfileData().subscribe(
      (response) => {
        this.userDetails = response;
        localStorage.setItem('userData', JSON.stringify(this.userDetails));
      },
      (error) => {
        console.error(error);
        Swal.fire({
          icon: 'error',
          text: error.error?.message || error.message,
          showConfirmButton: true,
          confirmButtonText: 'Ok',
        });
      },
    );
  }
  isRememberMe(): boolean {
    const rememberMeValue = localStorage.getItem('rememberMe');
    if (rememberMeValue !== null) {
      return JSON.parse(rememberMeValue);
    }
    return false;
  }

  onRememberMeChange(event: any) {
    this.rememberMe = event.target.checked;
  }
  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
    this.loginForm.reset();
  }
}